import React, { useState, useEffect } from "react";
import "react-simple-keyboard/build/css/index.css";
import { useTranslation } from "react-i18next";
import moment from "moment";
import { ApiKey, ApiUrl } from "../../util/Constant";
import ApiEngine from "../../util/ApiEngine";
import { stringIsNullOrEmpty, numberWithCurrencyFormat } from "../../util/Util";
import DynamicLoadTable from "../../components/custom/DynamicLoadTable";
import ContentHeader from "components/content/header";
import DateRangeFilter from "../../components/custom/DateRangeFilter";

const Deposits = (props) => {
  const { t } = useTranslation();
  const [depositsUrl, setDepositsUrl] = useState("");
  const [depositsData, setDepositsData] = useState([]);
  const [selectedDate, setSelectedDate] = useState("");
  const [startDate, setStartDate] = useState(moment().startOf("week").format("YYYY-MM-DD"));
  const [endDate, setEndDate] = useState(moment().endOf("week").format("YYYY-MM-DD"));

  useEffect(() => {
    getDepositsData(startDate, endDate);
  }, []);

  async function getDepositsData(start, end, selected = "") {
    // Using the Daily Summary Report API endpoint
    var apiUrl =
      ApiUrl._API_GET_DAILY_SUMMARY_REPORT +
      "?startDate=" +
      moment(start).format("YYYY-MM-DD") +
      "&endDate=" +
      moment(end).format("YYYY-MM-DD") +
      "&v=" +
      Date.now();

    if (!stringIsNullOrEmpty(selected)) {
      apiUrl += "&selectedDate=" + moment(selected).format("YYYY-MM-DD");
    }
    setDepositsUrl(apiUrl);
  }

  useEffect(() => {
    if (!stringIsNullOrEmpty(selectedDate)) {
      getDepositsData(startDate, endDate, selectedDate);
    }
  }, [selectedDate]);

  function getRecordFromDynamicLoad(data) {
    setDepositsData(data);
  }

  return (
    <>
      <section className="section-padding" id="deposits-report-section">
        <div className="container">
          <div className="row">
            <div className="col-12">
              <ContentHeader enableBackButton title={t("DEPOSITS")} />
              <hr className="right-title-hr" />
              <div className="template-box mt-3" id="deposits-report-box">
                <DateRangeFilter
                  startDate={startDate}
                  endDate={endDate}
                  setStartDate={setStartDate}
                  setEndDate={setEndDate}
                  onSave={(start, end) => {
                    setStartDate(moment(start));
                    setEndDate(moment(end));
                    getDepositsData(start, end);
                    setSelectedDate("");
                  }}
                  options={true}
                />
                <div className="card card-package">
                  <div className="card-body p-0" style={{ overflowX: "auto" }}>
                    <table className="table reportTable">
                      <thead>
                        <tr>
                          <th>{t("DATE")}</th>
                          <th style={{ textAlign: 'right' }}>{t("REGISTER")}</th>
                          <th style={{ textAlign: 'right' }}>{t("FIRST_DEPOSIT")}</th>
                          <th style={{ textAlign: 'right' }}>{t("DEPOSIT")}</th>
                          <th style={{ textAlign: 'right' }}>{t("WITHDRAWAL")}</th>
                          <th style={{ textAlign: 'right' }}>{t("ADJUSTMENT")}</th>
                          <th style={{ textAlign: 'right' }}>{t("TRANSFER")}</th>
                        </tr>
                      </thead>
                      <tbody>
                        <DynamicLoadTable
                          returnRecord={getRecordFromDynamicLoad}
                          pageSize={20}
                          apiQuery={depositsUrl}
                          isTable={true}
                          searchEnabled={true}
                          render={(item, i) => {
                            let isGrouped =
                              moment(endDate, "YYYY-MM-DD").diff(
                                moment(startDate, "YYYY-MM-DD"),
                                "days"
                              ) >= 1 && stringIsNullOrEmpty(selectedDate);
                            
                            // Format date from UniversalDate
                            const displayDate = moment(item["universalDate"]).format("MM/DD/YYYY");
                            
                            // Use profitLoss directly from API (already calculated)
                            const profitLoss = parseFloat(item["profitLoss"] || 0);
                            
                            return (
                              <tr key={"deposits_report_" + i}>
                                <td
                                  className={isGrouped ? "hoverable" : ""}
                                  onClick={() => {
                                    if (isGrouped) {
                                      setSelectedDate(moment(item["universalDate"]).format('YYYY-MM-DD'));
                                    }
                                  }}
                                >
                                  {displayDate}
                                </td>
                                <td style={{ textAlign: 'right' }}>{item["register"] || 0}</td>
                                <td style={{ textAlign: 'right' }}>
                                  {item["firstDepositCount"] || 0} / {item["firstDepositApprovalCount"] || 0} / {numberWithCurrencyFormat(parseFloat(item["firstDepositTotalAmount"] || 0), 3, true)}
                                </td>
                                <td style={{ textAlign: 'right' }}>
                                  {item["depositCount"] || 0} / {item["depositApprovalCount"] || 0} / {numberWithCurrencyFormat(parseFloat(item["depositTotalAmount"] || 0), 3, true)}
                                </td>
                                <td style={{ textAlign: 'right' }}>
                                  {item["withdrawalCount"] || 0} / {item["withdrawalApprovalCount"] || 0} / {numberWithCurrencyFormat(parseFloat(item["withdrawalTotalAmount"] || 0), 3, true)}
                                </td>
                                <td style={{ textAlign: 'right' }}>
                                  {item["adjustmentCount"] || 0} / {item["adjustmentApprovalCount"] || 0} / {numberWithCurrencyFormat(parseFloat(item["adjustmentTotalAmount"] || 0), 3, true)}
                                </td>
                                <td style={{ textAlign: 'right' }}>
                                  {item["transferCount"] || 0} / {item["transferApprovalCount"] || 0} / {numberWithCurrencyFormat(parseFloat(item["transferTotalAmount"] || 0), 3, true)}
                                </td>
                              </tr>
                            );
                          }}
                        />
                      </tbody>
                      <tfoot>
                        <tr>
                          <td>
                            <b>{t("TOTAL")}</b>
                          </td>
                          <td style={{ textAlign: 'right' }}>
                            <b>
                              {depositsData.reduce((total, data) => total + parseInt(data.register || 0), 0)}
                            </b>
                          </td>
                          <td style={{ textAlign: 'right' }}>
                            <b>
                              {depositsData.reduce((total, data) => total + parseInt(data.firstDepositCount || 0), 0)} / {depositsData.reduce((total, data) => total + parseInt(data.firstDepositApprovalCount || 0), 0)} / {numberWithCurrencyFormat(
                                parseFloat(
                                  depositsData.reduce(
                                    (total, data) => total + parseFloat(data.firstDepositTotalAmount || 0),
                                    0
                                  )
                                ),
                                3,
                                true
                              )}
                            </b>
                          </td>
                          <td style={{ textAlign: 'right' }}>
                            <b>
                              {depositsData.reduce((total, data) => total + parseInt(data.depositCount || 0), 0)} / {depositsData.reduce((total, data) => total + parseInt(data.depositApprovalCount || 0), 0)} / {numberWithCurrencyFormat(
                                parseFloat(
                                  depositsData.reduce(
                                    (total, data) => total + parseFloat(data.depositTotalAmount || 0),
                                    0
                                  )
                                ),
                                3,
                                true
                              )}
                            </b>
                          </td>
                          <td style={{ textAlign: 'right' }}>
                            <b>
                              {depositsData.reduce((total, data) => total + parseInt(data.withdrawalCount || 0), 0)} / {depositsData.reduce((total, data) => total + parseInt(data.withdrawalApprovalCount || 0), 0)} / {numberWithCurrencyFormat(
                                parseFloat(
                                  depositsData.reduce(
                                    (total, data) => total + parseFloat(data.withdrawalTotalAmount || 0),
                                    0
                                  )
                                ),
                                3,
                                true
                              )}
                            </b>
                          </td>
                          <td style={{ textAlign: 'right' }}>
                            <b>
                              {depositsData.reduce((total, data) => total + parseInt(data.adjustmentCount || 0), 0)} / {depositsData.reduce((total, data) => total + parseInt(data.adjustmentApprovalCount || 0), 0)} / {numberWithCurrencyFormat(
                                parseFloat(
                                  depositsData.reduce(
                                    (total, data) => total + parseFloat(data.adjustmentTotalAmount || 0),
                                    0
                                  )
                                ),
                                3,
                                true
                              )}
                            </b>
                          </td>
                          <td style={{ textAlign: 'right' }}>
                            <b>
                              {depositsData.reduce((total, data) => total + parseInt(data.transferCount || 0), 0)} / {depositsData.reduce((total, data) => total + parseInt(data.transferApprovalCount || 0), 0)} / {numberWithCurrencyFormat(
                                parseFloat(
                                  depositsData.reduce(
                                    (total, data) => total + parseFloat(data.transferTotalAmount || 0),
                                    0
                                  )
                                ),
                                3,
                                true
                              )}
                            </b>
                          </td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default Deposits;
